import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Users } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@scio3/ui';
import { CustomerSearchBox } from './CustomerSearchBox';
import { useStaffSearchUsers } from '../hooks/useStaffCustomer';

/**
 * Users column — search by user email or `@domain`.
 *
 * Rides `users.emails.address_1`. Row click navigates to the user's account,
 * same landing as Accounts column (staff inspects the account, not the user).
 */
export function UsersSearchColumn() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const { data, loading, error } = useStaffSearchUsers(query);
  const results = data?.staffSearchUsers ?? [];

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border bg-card p-4"
      data-testid="users-search-column"
    >
      <header className="flex items-center gap-2">
        <Users className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">{t('Users')}</h2>
      </header>
      <CustomerSearchBox
        initialValue={query}
        onSubmit={setQuery}
        placeholder={t('Email or @domain')}
        ariaLabel={t('Search users')}
        testId="users-search-box"
      />
      {error ? (
        <p className="text-xs text-destructive" data-testid="users-search-error">
          {t('Search failed.')} {error.message}
        </p>
      ) : loading ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{t('Searching…')}</p>
      ) : query.trim().length < 2 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{t('Type to search…')}</p>
      ) : results.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{t('No results.')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('User email')}</TableHead>
              <TableHead>{t('Account')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {results.map((r) => (
              <TableRow
                key={r.userId}
                className="hover:bg-accent/50 transition-colors"
                data-testid="users-search-result-row"
              >
                <TableCell className="font-medium">
                  {r.accountId ? (
                    <Link
                      to={`/staff/customers/${encodeURIComponent(r.accountId)}/devices`}
                      className="hover:underline"
                    >
                      {r.email}
                    </Link>
                  ) : (
                    <span>{r.email}</span>
                  )}
                </TableCell>
                <TableCell className="text-xs">
                  <div>{r.companyName ?? '—'}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{r.accountId}</div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
